import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Field,
  Input,
  MessageBar,
  MessageBarBody,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  Text,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { GL_ROLES } from '../types';
import type { ClientJournalTemplate, GlAccount, PosReportReadResult, TemplateLine, TemplateLineSide, TemplateLineSource } from '../types';
import { MAX_ROW_LABEL, displayName, findAccount, pickable } from '../lib/glChart';
import {
  DEFAULT_TEMPLATE_LINES, MAX_HINT, TEMPLATE_SOURCES, lineLabel, ownTemplate, sideForAccountType, sourceForKey,
} from '../lib/clientTemplate';
import { lineKeyFor } from '../lib/glOneList';
import {
  GRATUITY_CODE, SPOTON_SECTIONS, formatUsd, gratuityOwnLine, hintFor, previewOf, printedAt, proposeFromReport,
  proposeStandardTemplate, type Preview,
} from '../lib/posRegistry';
import UsesPicker from './UsesPicker';

const useStyles = makeStyles({
  section: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalS },
  heading: { fontSize: tokens.fontSizeBase400, fontWeight: tokens.fontWeightSemibold },
  muted: { color: tokens.colorNeutralForeground3 },
  small: { color: tokens.colorNeutralForeground3, fontSize: tokens.fontSizeBase200 },
  fill: { width: '100%', minWidth: 0 },
  colLabel: { width: '18%' },
  colAccount: { width: '17%' },
  colSide: { width: '10%' },
  colSource: { width: '13%' },
  colHint: { width: '28%' },
  colOrder: { width: '14%' },
  cellStack: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXXS },
  actions: { display: 'flex', gap: tokens.spacingHorizontalXXS, flexWrap: 'wrap' },
  add: { display: 'grid', gridTemplateColumns: '2fr 2fr 1fr 1fr auto', gap: tokens.spacingHorizontalS, alignItems: 'end' },
  bar: { display: 'flex', gap: tokens.spacingHorizontalS, flexWrap: 'wrap' },
});

/** A line's note, as the server allows (JournalTemplateRules.MaxNoteLength). */
const MAX_NOTE = 300;

/** The add picker's value for "a line of the client's own" (never a role key). */
const OWN = '__own__';

export interface TemplateValue {
  journalTemplate: ClientJournalTemplate | null;
  accounts: GlAccount[];
  roles: Record<string, string>;
  rowLabels: Record<string, string>;
}

/** The client's latest POS report, as the template reads it (wave 4 §3–§4): what it is, and what reading it said. */
export interface TemplateReport {
  fileName: string | null;
  read: PosReportReadResult | undefined;
  loading: boolean;
  failed: boolean;
}

/**
 * The client's journal template (ruled by Christian 2026-10-29, docs/client-journal-template.md §A): the lines a POS
 * journal for this client is made of, each with its label, its account (the same mapping as the list above: one place),
 * its side, who fills it (POS, the sales tax return, the bank, or typed) and which part of the report fills it. **The
 * template is the extraction target.** Without one, the client has the DEFAULT, the seven POS boxes. Saved with Save.
 *
 * Wave 4 (ptc-docs plan 2026-11-02): one key per name (§1), the grouped "Add a line" (§1b), the account fills the line's
 * name, side and source (§2), the standard and from-the-report proposals (§3), and the report part as a picker with a
 * preview (§4).
 */
export default function JournalTemplateSection({
  value, onChange, report,
}: { value: TemplateValue; onChange: (next: TemplateValue) => void; report?: TemplateReport }) {
  const styles = useStyles();
  const { t, i18n } = useTranslation();
  const own = ownTemplate(value.journalTemplate);
  const lines = own ?? [...DEFAULT_TEMPLATE_LINES];
  const role = (key: string) => t(`glMappingPage.fields.${key}`, { defaultValue: key });
  const labelOf = (line: TemplateLine) => lineLabel(line, value.rowLabels, role);
  const accounts = pickable(value.accounts);

  const [addKey, setAddKey] = useState(OWN);
  const [addLabel, setAddLabel] = useState('');
  const [addSide, setAddSide] = useState<TemplateLineSide>('credit');
  const [addSource, setAddSource] = useState<TemplateLineSource>('pos');
  const [addTried, setAddTried] = useState(false);
  // Lines whose report part is typed ("Other — type it"), by key; a line with a hint and no picks opens typed.
  const [typing, setTyping] = useState<ReadonlySet<string>>(new Set());
  const [proposed, setProposed] = useState<{ kind: 'standard' | 'report'; added: number } | null>(null);

  const setLines = (next: TemplateLine[]) => onChange({ ...value, journalTemplate: { lines: next } });

  function update(index: number, patch: Partial<TemplateLine>) {
    const line = lines[index];
    const next = lines.map((l, i) => (i === index ? { ...l, ...patch } : l));
    // A client's own line keeps the list's label in step, so both screens call it the same thing.
    if (patch.label !== undefined && !(GL_ROLES as readonly string[]).includes(line.key) && patch.label?.trim()) {
      onChange({ ...value, journalTemplate: { lines: next }, rowLabels: { ...value.rowLabels, [line.key]: patch.label.trim() } });
      return;
    }
    setLines(next);
  }

  /**
   * A line's account IS its mapping. One step (wave 4 §2): on an own template, a blank name takes the account's, the side
   * follows the account's type (when it has one), and "filled from" follows the role (a typed or bank line keeps its own).
   */
  function setAccount(index: number, code: string) {
    const line = lines[index];
    const { [line.key]: _was, ...rest } = value.roles;
    const roles = code ? { ...rest, [line.key]: code } : rest;
    if (!own || !code) {
      onChange({ ...value, roles });
      return;
    }
    const account = findAccount(value.accounts, code);
    const name = account ? displayName(account) : '';
    const patch: Partial<TemplateLine> = { source: sourceForKey(line.key, line.source) };
    const side = sideForAccountType(account?.type);
    if (side) patch.side = side;
    if (!line.label?.trim() && name) patch.label = name;
    const next = lines.map((l, i) => (i === index ? { ...l, ...patch } : l));
    const custom = !(GL_ROLES as readonly string[]).includes(line.key);
    onChange({
      ...value,
      roles,
      journalTemplate: { lines: next },
      rowLabels: custom && patch.label && !value.rowLabels[line.key] ? { ...value.rowLabels, [line.key]: patch.label } : value.rowLabels,
    });
  }

  function move(index: number, by: -1 | 1) {
    const next = [...lines];
    const [line] = next.splice(index, 1);
    next.splice(index + by, 0, line);
    setLines(next);
  }

  // "Add a line" (§1b): the client's own lines not in the template, the standard roles not in it, then a new line.
  const inTemplate = new Set(lines.map((l) => l.key));
  const ownFree = Object.keys(value.rowLabels).filter((k) => !inTemplate.has(k));
  const standardFree = GL_ROLES.filter((k) => !inTemplate.has(k));
  const addOptions = [
    ...ownFree.map((k) => ({ value: k, label: value.rowLabels[k], group: t('glMappingPage.oneList.groups.own') })),
    ...standardFree.map((k) => ({ value: k, label: role(k), group: t('glMappingPage.oneList.groups.standard') })),
    { value: OWN, label: t('glMappingPage.template.addOwn'), group: t('glMappingPage.template.addNew') },
  ];
  const addOk = addKey !== OWN || (addLabel.trim().length > 0 && addLabel.trim().length <= MAX_ROW_LABEL);

  /** Picking a line to add fills its side and source from its account and role (§2). */
  function pickAdd(key: string) {
    setAddKey(key);
    if (key === OWN) return;
    const account = findAccount(value.accounts, value.roles[key] ?? '');
    const side = sideForAccountType(account?.type);
    if (side) setAddSide(side);
    setAddSource(sourceForKey(key, addSource));
  }

  function addLine() {
    setAddTried(true);
    if (!addOk) return;
    let key = addKey;
    let rowLabels = value.rowLabels;
    let label: string | null = addLabel.trim() || null;
    if (addKey === OWN) {
      // One key per name (wave 4 §1.2.1): typing a name the client already has adds THAT line, never a second key.
      const found = lineKeyFor(addLabel, value.rowLabels, [...Object.keys(value.roles), ...Object.keys(value.rowLabels), ...lines.map((l) => l.key)]);
      key = found.key;
      if (found.existing) label = value.rowLabels[key];
      else rowLabels = { ...rowLabels, [key]: addLabel.trim() };
    } else if (value.rowLabels[addKey]) {
      label = label ?? value.rowLabels[addKey];
    }
    if (lines.some((l) => l.key === key)) {
      setAddKey(OWN);
      setAddLabel('');
      setAddTried(false);
      return;
    }
    const line: TemplateLine = { key, label, side: addSide, source: addSource };
    onChange({ ...value, journalTemplate: { lines: [...lines, line] }, rowLabels });
    setAddKey(OWN);
    setAddLabel('');
    setAddTried(false);
  }

  /** §3: the standard lines, into the form; nothing is saved until Save. */
  function startStandard() {
    const p = proposeStandardTemplate(lines, value.roles, value.rowLabels, (id) => t(`glMappingPage.template.standard.classes.${id}`),
      (id) => (id === 'gratuity' ? t('glMappingPage.template.standard.gratuityNote') : null));
    onChange({ ...value, journalTemplate: { lines: p.lines }, rowLabels: p.rowLabels });
    setProposed({ kind: 'standard', added: p.added });
  }

  /** §7: the one-click split. Gratuity leaves this line for the standard Gratuity line, right after it. The form only. */
  function splitGratuity(index: number) {
    const p = gratuityOwnLine(lines, index, value.roles, value.rowLabels,
      t('glMappingPage.template.standard.classes.gratuity'), t('glMappingPage.template.standard.gratuityNote'));
    onChange({ ...value, journalTemplate: { lines: p.lines }, rowLabels: p.rowLabels });
  }

  /** §3: one sales line per revenue class the report prints that no line reads yet. */
  function addFromReport() {
    if (!report?.read) return;
    const p = proposeFromReport(lines, value.roles, value.rowLabels, report.read.classesPrinted,
      (label) => t('glMappingPage.template.fromReport.lineName', { label }));
    onChange({ ...value, journalTemplate: { lines: p.lines }, rowLabels: p.rowLabels });
    setProposed({ kind: 'report', added: p.added });
  }

  /** The report part's options (§4): the registry's printed sections and labels, then any pick the registry lacks. */
  function partOptions(picked: readonly string[]) {
    const known = SPOTON_SECTIONS.flatMap((s) => s.lines.map((l) => ({ value: l.code, label: l.label, group: s.section })));
    const unknown = picked.filter((c) => !printedAt(c)).map((c) => ({
      value: c, label: c, group: t('glMappingPage.template.part.notOnReport'), dimmed: true,
    }));
    return [...known, ...unknown];
  }

  function previewText(p: Preview): string {
    switch (p.kind) {
      case 'noReport': return t('glMappingPage.template.preview.noReport');
      case 'unknownKind': return t('glMappingPage.template.preview.unknownKind');
      case 'notReadYet': return t('glMappingPage.template.preview.notReadYet', { report: p.report });
      case 'nothingPicked': return t('glMappingPage.template.preview.nothingPicked');
      case 'notPrinted': return t('glMappingPage.template.preview.notPrinted', { parts: hintFor(p.codes) });
      case 'reads': return p.month
        ? t('glMappingPage.template.preview.reads', { amount: formatUsd(p.amount, i18n.language), month: p.month })
        : t('glMappingPage.template.preview.readsNoMonth', { amount: formatUsd(p.amount, i18n.language) });
    }
  }

  function preview(picked: readonly string[]): string {
    if (report?.loading) return t('glMappingPage.template.preview.reading');
    if (report?.failed) return t('glMappingPage.template.preview.failed');
    return previewText(previewOf(picked, report?.fileName ? report.read : null, i18n.language));
  }

  function partCell(line: TemplateLine, i: number, label: string) {
    const picked = line.registryField ?? [];
    const typed = typing.has(line.key) || (!!line.hint?.trim() && picked.length === 0);
    return (
      <div className={styles.cellStack}>
        <UsesPicker
          label={t('glMappingPage.template.part.of', { line: label })}
          placeholder={t('glMappingPage.template.part.none')}
          options={partOptions(picked)}
          selected={picked}
          onChange={(codes) => {
            setTyping((s) => { const n = new Set(s); n.delete(line.key); return n; });
            update(i, { registryField: codes.length ? codes : null, hint: codes.length ? hintFor(codes) : null });
          }}
          action={{
            label: t('glMappingPage.template.part.other'),
            onClick: () => setTyping((s) => new Set(s).add(line.key)),
          }}
        />
        {picked.includes(GRATUITY_CODE) && picked.length > 1 && (
          <Button size="small" appearance="subtle" aria-label={t('glMappingPage.template.gratuityOwnLineOf', { line: label })} onClick={() => splitGratuity(i)}>
            {t('glMappingPage.template.gratuityOwnLine')}
          </Button>
        )}
        {typed && (
          <Input
            className={styles.fill}
            size="small"
            maxLength={MAX_HINT}
            aria-label={t('glMappingPage.template.hintOf', { line: label })}
            placeholder={t('glMappingPage.template.hintPlaceholder')}
            value={line.hint ?? ''}
            onChange={(_e, data) => update(i, { hint: data.value, registryField: null })}
          />
        )}
        {line.source === 'pos' && (
          <Text className={styles.small} aria-label={t('glMappingPage.template.preview.of', { line: label })}>
            {typed && picked.length === 0 ? t('glMappingPage.template.preview.typed') : preview(picked)}
          </Text>
        )}
      </div>
    );
  }

  const canFromReport = !!own && report?.read?.status !== undefined && report.read.status !== 'unknownKind'
    && report.read.classesPrinted.some((c) => c.code);

  return (
    <section className={styles.section} aria-labelledby="journal-template-heading">
      <Text as="h2" id="journal-template-heading" className={styles.heading}>{t('glMappingPage.template.heading')}</Text>
      <Text className={styles.muted}>{t(own ? 'glMappingPage.template.introOwn' : 'glMappingPage.template.introDefault')}</Text>
      {report?.fileName && (
        <Text className={styles.small}>{t('glMappingPage.template.reportNote', { file: report.fileName })}</Text>
      )}

      {proposed && (
        <MessageBar intent="info">
          <MessageBarBody>
            {proposed.added > 0
              ? t(`glMappingPage.template.proposed.${proposed.kind}`, { count: proposed.added })
              : t(`glMappingPage.template.proposed.${proposed.kind}None`)}
          </MessageBarBody>
        </MessageBar>
      )}

      <Table size="small" aria-label={t('glMappingPage.template.heading')}>
        <TableHeader>
          <TableRow>
            <TableHeaderCell className={styles.colLabel}>{t('glMappingPage.template.columns.label')}</TableHeaderCell>
            <TableHeaderCell className={styles.colAccount}>{t('glMappingPage.template.columns.account')}</TableHeaderCell>
            <TableHeaderCell className={styles.colSide}>{t('glMappingPage.template.columns.side')}</TableHeaderCell>
            <TableHeaderCell className={styles.colSource}>{t('glMappingPage.template.columns.source')}</TableHeaderCell>
            <TableHeaderCell className={styles.colHint}>{t('glMappingPage.template.columns.part')}</TableHeaderCell>
            <TableHeaderCell className={styles.colOrder}>{own ? t('glMappingPage.template.columns.order') : ''}</TableHeaderCell>
          </TableRow>
        </TableHeader>
        <TableBody>
          {lines.map((line, i) => {
            const label = labelOf(line);
            const code = value.roles[line.key] ?? '';
            return (
              <TableRow key={line.key}>
                <TableCell>
                  {own ? (
                    <div className={styles.cellStack}>
                      <Input
                        className={styles.fill}
                        size="small"
                        aria-label={t('glMappingPage.template.labelOf', { line: label })}
                        placeholder={role(line.key)}
                        value={line.label ?? ''}
                        onChange={(_e, data) => update(i, { label: data.value })}
                      />
                      <Input
                        className={styles.fill}
                        size="small"
                        maxLength={MAX_NOTE}
                        aria-label={t('glMappingPage.template.noteOf', { line: label })}
                        placeholder={t('glMappingPage.template.notePlaceholder')}
                        value={line.note ?? ''}
                        onChange={(_e, data) => update(i, { note: data.value || null })}
                      />
                    </div>
                  ) : (
                    <div className={styles.cellStack}>
                      <span>{label}</span>
                      {line.note && <Text className={styles.small}>{line.note}</Text>}
                    </div>
                  )}
                </TableCell>
                <TableCell>
                  <Select
                    className={styles.fill}
                    size="small"
                    aria-label={t('glMappingPage.template.accountOf', { line: label })}
                    value={code}
                    onChange={(_e, data) => setAccount(i, data.value)}
                  >
                    <option value="">{t('glMappingPage.template.notMapped')}</option>
                    {code && !accounts.some((a) => a.code === code) && <option value={code}>{code}</option>}
                    {accounts.map((a) => (
                      <option key={a.code} value={a.code}>{displayName(a) ? `${a.code} · ${displayName(a)}` : a.code}</option>
                    ))}
                  </Select>
                </TableCell>
                <TableCell>
                  {own ? (
                    <Select
                      className={styles.fill}
                      size="small"
                      aria-label={t('glMappingPage.template.sideOf', { line: label })}
                      value={line.side}
                      onChange={(_e, data) => update(i, { side: data.value as TemplateLineSide })}
                    >
                      <option value="credit">{t('glMappingPage.template.sides.credit')}</option>
                      <option value="debit">{t('glMappingPage.template.sides.debit')}</option>
                    </Select>
                  ) : t(`glMappingPage.template.sides.${line.side}`)}
                </TableCell>
                <TableCell>
                  {own ? (
                    <Select
                      className={styles.fill}
                      size="small"
                      aria-label={t('glMappingPage.template.sourceOf', { line: label })}
                      value={line.source}
                      onChange={(_e, data) => update(i, { source: data.value as TemplateLineSource })}
                    >
                      {TEMPLATE_SOURCES.map((s) => <option key={s} value={s}>{t(`glMappingPage.template.sources.${s}`)}</option>)}
                    </Select>
                  ) : t(`glMappingPage.template.sources.${line.source}`)}
                </TableCell>
                <TableCell>
                  {own ? partCell(line, i, label) : (
                    <Text className={styles.muted}>{hintFor(line.registryField ?? [])}</Text>
                  )}
                </TableCell>
                <TableCell>
                  {own && (
                    <div className={styles.actions}>
                      <Button size="small" appearance="subtle" disabled={i === 0} aria-label={t('glMappingPage.template.upOf', { line: label })} onClick={() => move(i, -1)}>↑</Button>
                      <Button size="small" appearance="subtle" disabled={i === lines.length - 1} aria-label={t('glMappingPage.template.downOf', { line: label })} onClick={() => move(i, 1)}>↓</Button>
                      <Button size="small" appearance="subtle" aria-label={t('glMappingPage.template.removeOf', { line: label })} onClick={() => setLines(lines.filter((_l, j) => j !== i))}>
                        {t('glMappingPage.template.remove')}
                      </Button>
                    </div>
                  )}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>

      {own && (
        <div className={styles.add}>
          <Field label={t('glMappingPage.template.addWhich')}>
            <UsesPicker
              single
              label={t('glMappingPage.template.addWhich')}
              placeholder={t('glMappingPage.template.addOwn')}
              options={addOptions}
              selected={[addKey]}
              onChange={(next) => pickAdd(next[0] ?? OWN)}
            />
          </Field>
          <Field
            label={t('glMappingPage.template.columns.label')}
            validationState={addTried && !addOk ? 'error' : 'none'}
            validationMessage={addTried && !addOk ? t('glMappingPage.addRowProblem', { max: MAX_ROW_LABEL }) : undefined}
          >
            <Input value={addLabel} onChange={(_e, data) => setAddLabel(data.value)} />
          </Field>
          <Field label={t('glMappingPage.template.columns.side')}>
            <Select value={addSide} onChange={(_e, data) => setAddSide(data.value as TemplateLineSide)}>
              <option value="credit">{t('glMappingPage.template.sides.credit')}</option>
              <option value="debit">{t('glMappingPage.template.sides.debit')}</option>
            </Select>
          </Field>
          <Field label={t('glMappingPage.template.columns.source')}>
            <Select value={addSource} onChange={(_e, data) => setAddSource(data.value as TemplateLineSource)}>
              {TEMPLATE_SOURCES.map((s) => <option key={s} value={s}>{t(`glMappingPage.template.sources.${s}`)}</option>)}
            </Select>
          </Field>
          <Button onClick={addLine}>{t('glMappingPage.template.addLine')}</Button>
        </div>
      )}

      <div className={styles.bar}>
        {own ? (
          <Button onClick={() => onChange({ ...value, journalTemplate: null })}>{t('glMappingPage.template.useDefault')}</Button>
        ) : (
          <Button onClick={() => setLines(DEFAULT_TEMPLATE_LINES.map((l) => ({ ...l })))}>{t('glMappingPage.template.startOwn')}</Button>
        )}
        <Button onClick={startStandard}>{t('glMappingPage.template.standard.start')}</Button>
        {canFromReport && <Button onClick={addFromReport}>{t('glMappingPage.template.fromReport.add')}</Button>}
      </div>
      {own && report?.read?.status === 'unknownKind' && (
        <Text className={styles.small}>{t('glMappingPage.template.fromReport.unknownKind')}</Text>
      )}
      {own && report?.read && report.read.classesPrinted.some((c) => !c.code) && (
        <Text className={styles.small}>
          {t('glMappingPage.template.fromReport.uncoded', { classes: report.read.classesPrinted.filter((c) => !c.code).map((c) => c.label).join(', ') })}
        </Text>
      )}
    </section>
  );
}
